import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { typography } from '@/common/styles/typographyTokens';
import { semanticColors } from '@/common/styles/colorTokens';
import { sizes } from '@/common/styles/sizes';

export interface ToggleSwitchProps {
  /**
   * 토글 스위치의 선택 상태
   */
  checked?: boolean;
  
  /**
   * 초기 선택 상태 (비제어 컴포넌트로 사용 시)
   */
  defaultChecked?: boolean;
  
  /**
   * 값이 변경될 때 호출되는 함수
   */
  onChange?: (checked: boolean) => void;
  
  /**
   * 토글 스위치와 함께 표시할 레이블
   */
  label?: React.ReactNode;
  
  /**
   * 비활성화 여부
   */
  disabled?: boolean;
  
  /**
   * 에러 상태 여부
   */
  error?: boolean;
  
  /**
   * 필수 항목 여부
   */
  required?: boolean;
  
  /**
   * 토글 스위치의 이름
   */
  name?: string;
  
  /**
   * 토글 스위치의 값
   */
  value?: string;
  
  /**
   * 추가 CSS 클래스
   */
  className?: string;
  
  /**
   * 토글 스위치의 ID
   */
  id?: string;
  
  /**
   * 크기 (default: medium)
   */
  size?: 'small' | 'medium' | 'large';
}

/**
 * ToggleSwitch 컴포넌트
 * 
 * 온/오프 상태를 시각적으로 표현하는 토글 스위치 컴포넌트입니다.
 * 세 가지 크기와 비활성화, 에러 상태를 지원합니다.
 */
export const ToggleSwitch = ({
  checked,
  defaultChecked,
  onChange,
  label,
  disabled = false,
  error = false,
  required = false,
  name,
  value,
  className,
  id,
  size = 'medium',
}: ToggleSwitchProps) => {
  const [isChecked, setIsChecked] = useState<boolean>(
    checked !== undefined ? checked : defaultChecked || false
  );
  
  useEffect(() => {
    if (checked !== undefined) {
      setIsChecked(checked);
    }
  }, [checked]);
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCheckedState = event.target.checked;
    
    if (checked === undefined) {
      setIsChecked(newCheckedState);
    }
    
    onChange?.(newCheckedState);
  };
  
  return (
    <ToggleSwitchContainer className={className} onClick={(e) => {
      e.stopPropagation();
    }}>
      <ToggleSwitchWrapper 
        disabled={disabled}
        error={error}
      >
        <HiddenInput
          type="checkbox"
          id={id}
          name={name}
          value={value}
          checked={isChecked}
          onChange={handleChange}
          disabled={disabled}
        />
        <StyledToggle 
          checked={isChecked}
          disabled={disabled}
          error={error}
          size={size}
        >
          <Slider />
        </StyledToggle>
        
        {label && (
          <LabelText disabled={disabled}>
            {label}
            {required && <RequiredMark>*</RequiredMark>}
          </LabelText>
        )}
      </ToggleSwitchWrapper>
    </ToggleSwitchContainer>
  );
};

// 스타일드 컴포넌트
const ToggleSwitchContainer = styled.div`
  display: inline-flex;
  vertical-align: middle;
`;

const ToggleSwitchWrapper = styled.label<{ 
  disabled?: boolean;
  error?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  gap: ${sizes.gap[8]};
  user-select: none;
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
  margin: 0;
`;

const StyledToggle = styled.div<{
  checked: boolean;
  disabled?: boolean;
  error?: boolean;
  size: 'small' | 'medium' | 'large';
}>`
  position: relative;
  display: inline-block;
  transition: background-color 0.3s;
  border-radius: 999px;
  background-color: ${props => {
    if (props.disabled) {
      return semanticColors.background.disabled;
    }
    if (props.checked) {
      return semanticColors.background.brand;
    }
    if (props.error) {
      return semanticColors.background.danger.secondary;
    }
    return semanticColors.background.tertiary;
  }};
  
  ${props => {
    if (props.size === 'small') {
      return css`
        width: 36px;
        height: 20px;
      `;
    }
    if (props.size === 'large') {
      return css`
        width: 60px;
        height: 32px;
      `;
    }
    return css`
      width: 48px;
      height: 24px;
    `;
  }}
  
  /* Focus outline for accessibility */
  &:focus-within {
    box-shadow: 0 0 0 2px ${semanticColors.border.brand};
  }
`;

const Slider = styled.span`
  position: absolute;
  top: 2px;
  left: 2px;
  bottom: 2px;
  aspect-ratio: 1/1;
  border-radius: 50%;
  background-color: ${semanticColors.text.white};
  transition: all 0.3s;
  
  ${HiddenInput}:checked + ${StyledToggle} & {
    transform: translateX(calc(100% + 4px));
  }
  
  ${HiddenInput}:disabled + ${StyledToggle} & {
    background-color: ${semanticColors.background.disabled};
  }
`;

const LabelText = styled.span<{ disabled?: boolean }>`
  ${typography.paragraph.p2.default};
  color: ${props => 
    props.disabled ? semanticColors.text.disabled : semanticColors.text.secondary
  };
`;

const RequiredMark = styled.span`
  color: ${semanticColors.border.dangerPrimary};
  margin-left: ${sizes.margin[4]};
`;

export default ToggleSwitch; 